import React, { useState, useEffect } from 'react';
import { SPLASH_CONFIG } from '../../config/splashConfig';

const AuthLoadingScreen = ({ 
  minDisplayTime = SPLASH_CONFIG.minDisplayTime, 
  onLoadComplete 
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (SPLASH_CONFIG.animations.enableProgressBar) {
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 95) return prev;
          return prev + Math.random() * 15;
        });
      }, 200);

      const completeTimer = setTimeout(() => {
        setProgress(100);
        clearInterval(progressInterval);
      }, minDisplayTime - 300);

      return () => {
        clearInterval(progressInterval);
        clearTimeout(completeTimer);
      };
    }
  }, [minDisplayTime]);

  useEffect(() => {
    const minTimer = setTimeout(() => {
      if (onLoadComplete) {
        onLoadComplete();
      }
      setFadeOut(true);
      setTimeout(() => setIsVisible(false), SPLASH_CONFIG.animations.fadeOutDuration);
    }, minDisplayTime);

    return () => clearTimeout(minTimer);
  }, [minDisplayTime, onLoadComplete]);

  if (!isVisible) return null;

  return (
    <div 
      className={`fixed inset-0 flex flex-col items-center justify-center z-[9999] overflow-hidden transition-opacity duration-700 ease-in-out ${
        fadeOut ? 'opacity-0' : 'opacity-100'
      }`}
      style={{ 
        background: `linear-gradient(135deg, ${SPLASH_CONFIG.gradientColors.start} 0%, ${SPLASH_CONFIG.gradientColors.middle} 50%, ${SPLASH_CONFIG.gradientColors.end} 100%)`
      }}
    >
      {SPLASH_CONFIG.animations.enableParticles && (
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-emerald-600/10 rounded-full blur-[120px] animate-pulse" style={{ animationDuration: '8s' }}></div>
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal-600/10 rounded-full blur-[100px] animate-pulse" style={{ animationDuration: '10s', animationDelay: '2s' }}></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-emerald-500/5 rounded-full blur-[90px] animate-pulse" style={{ animationDuration: '12s', animationDelay: '4s' }}></div>
        </div>
      )}

      <div className="relative z-10 flex flex-col items-center px-8 w-full max-w-md mx-auto">
        
        <div className="relative mb-12 group flex justify-center items-center">
          <img
            src={SPLASH_CONFIG.logo.src}
            alt={SPLASH_CONFIG.logo.alt}
            style={{ height: SPLASH_CONFIG.logo.height }}
            className="w-auto object-contain relative z-10 drop-shadow-xl transition-all duration-500 hover:scale-105"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          />
          <div className="hidden text-4xl font-black tracking-tight text-white relative z-10">
            {SPLASH_CONFIG.logo.fallbackText}
          </div>
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-400/20 to-teal-500/20 rounded-full blur-2xl transform scale-150 opacity-60 group-hover:opacity-80 transition-opacity duration-500"></div>
        </div>

        <div className="mb-10 relative">
          <div className="w-16 h-16 border-4 border-white/10 border-t-white rounded-full animate-spin"></div>
          <div className="absolute inset-0 bg-emerald-400/10 rounded-full blur-xl animate-pulse"></div>
        </div>

        <div className="text-center space-y-4 w-full">
          <h1 className="text-3xl font-bold text-white tracking-tight">
            {SPLASH_CONFIG.content.title}
          </h1>
          
          <p className="text-lg text-emerald-100 font-medium">
            {SPLASH_CONFIG.content.subtitle}
          </p>
          
          <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-white/30 to-transparent rounded-full mx-auto my-6"></div>

          <p className="text-sm text-emerald-300 max-w-[260px] mx-auto leading-relaxed">
            {SPLASH_CONFIG.content.description}
          </p>

          {SPLASH_CONFIG.animations.enableProgressBar && (
            <div className="mt-8 w-full max-w-xs mx-auto">
              <div className="h-1 bg-white/10 rounded-full overflow-hidden backdrop-blur-sm">
                <div 
                  className="h-full bg-gradient-to-r from-emerald-400 to-teal-400 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="text-xs text-white/40 mt-2 font-medium">
                {Math.round(progress)}%
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="absolute bottom-8 left-0 right-0 text-center">
        <p className="text-xs text-white/30 font-medium tracking-wider uppercase">
          Securing Data on the Ground
        </p>
      </div>
    </div>
  );
};

export default AuthLoadingScreen;